import { Card, CardContent, CardHeader, Box, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatValue, pickSeriesColor, type BaseChartConfig, type Widget } from "./types";

const AXIS_COLOR = "#374151";
const GRID_COLOR = "rgba(0,0,0,0.08)";
const TOOLTIP_BG = "rgba(15, 23, 42, 0.92)";
const TOOLTIP_TEXT = "#f9fafb";

type Point = { label: string; value: number } & Record<string, unknown>;

export function AreaCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as BaseChartConfig;
  const sparkline = widget.kind === "sparkline";
  const rawPoints = widget.data?.points ?? [];
  const points: Point[] = rawPoints
    .filter((p) => p && typeof p === "object")
    .map((p) => {
      const o = p as Record<string, unknown>;
      const label = String(o.label ?? o.name ?? o.x ?? "");
      const value =
        typeof o.value === "number" ? o.value
          : typeof o.y === "number" ? o.y
          : Number(o.value ?? o.y ?? 0);
      return { ...o, label, value };
    });
  const color = pickSeriesColor(cfg);
  const fmt = cfg.format ?? "number";

  const body = (
    <ResponsiveContainer width="100%" height={sparkline ? 80 : 400}>
      <AreaChart
        data={points}
        margin={
          sparkline
            ? { top: 4, right: 4, left: 4, bottom: 4 }
            : { top: 8, right: 24, left: 24, bottom: 32 }
        }
      >
        {!sparkline && <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />}
        <XAxis
          dataKey="label"
          hide={sparkline}
          tick={{ fill: AXIS_COLOR, fontSize: 11 }}
          stroke={AXIS_COLOR}
          label={cfg.x_label && !sparkline ? { value: cfg.x_label, position: "insideBottom", offset: -4, fill: AXIS_COLOR } : undefined}
        />
        <YAxis
          hide={sparkline}
          tick={{ fill: AXIS_COLOR, fontSize: 11 }}
          stroke={AXIS_COLOR}
          tickFormatter={(v: number) => formatValue(v, fmt)}
          label={cfg.y_label && !sparkline ? { value: cfg.y_label, angle: -90, position: "insideLeft", fill: AXIS_COLOR } : undefined}
        />
        {!sparkline && (
          <Tooltip
            contentStyle={{
              backgroundColor: TOOLTIP_BG, border: "none",
              borderRadius: 6, color: TOOLTIP_TEXT,
            }}
            labelStyle={{ color: TOOLTIP_TEXT, fontWeight: 600 }}
            itemStyle={{ color: TOOLTIP_TEXT }}
            formatter={(v: number) => [formatValue(v, fmt), cfg.y_label ?? "Value"]}
          />
        )}
        {cfg.target_line !== undefined && !sparkline && (
          <ReferenceLine
            y={cfg.target_line}
            stroke="#ef4444"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{ value: `Target ${formatValue(cfg.target_line, fmt)}`, fill: "#ef4444", fontSize: 11, position: "insideTopRight" }}
          />
        )}
        <defs>
          <linearGradient id={`area-grad-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <Area
          dataKey="value"
          stroke={color}
          fill={`url(#area-grad-${widget.id})`}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  if (sparkline) return body;

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        {points.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {widget.data?.error ? `No data (${widget.data.error})` : "No data."}
            </Typography>
          </Box>
        ) : (
          body
        )}
      </CardContent>
    </Card>
  );
}
